
import React, { useContext } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Bug, Globe } from 'lucide-react';
import { useFlags } from '@flagsmith/flagsmith/react';

import { Auth, useLoggedInUser } from 'src/services/api';
import { useSetPresence } from 'src/services/api/users/presence';
import { EPresence, isUserAway } from 'src/api/users-api';
import { LANDLORD_HOME_PATH } from 'src/routes';
import { Flex, FlexColumn } from 'src/components/flex';
import Link from 'src/components/link/Link';
import NavigationLink from 'src/components/layout/landlord/NavigationLink';
import PresenceDot from 'src/components/PresenceDot';
import { LogOutIcon } from 'src/icons';
import { links } from 'src/components/layout/landlord/links';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import { DesktopNavigationInboxesLink } from 'src/components/layout/landlord/DesktopNavigationInboxesLink';
import { DesktopNavigationPropertiesLink } from 'src/components/layout/landlord/DesktopNavigationPropertiesLink';
import { DesktopNavigationTeamLink } from 'src/components/layout/landlord/DesktopNavigationTeamLink';
import { EFeatureFlag, FORCE_TEAM_AVAILABILITY } from 'src/utils/feature-flags';
import { AuthenticatedStateContext } from 'src/authenticated-state/context';
import { getTimezoneAbbr } from 'src/utils/format-date-time';

import navbarStyles from 'src/components/layout/landlord/DesktopNavigationSidebar.module.css';
import reffieLogosBrainTransparent256 from 'src/assets/img/reffie-logos/brain-transparent-256.png';

type Props = {
    closeNavigationDrawer: () => void;
}

export default function MobileNavigationDrawer({
    closeNavigationDrawer
}: Props) {
    const handleLogout = () => {
        closeNavigationDrawer();
        Auth.logout();
    };
    const navigate = useNavigate();
    const flags = useFlags([EFeatureFlag.TEAM_AVAILABILITY]);

    const { data: loggedInUser } = useLoggedInUser();
    const authenticatedState = useContext(AuthenticatedStateContext);
    const timezoneOverride = authenticatedState?.userSettings?.timezone_override ?? null;
    const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneLabel = timezoneOverride ?
        `${timezoneOverride} (${getTimezoneAbbr(timezoneOverride)})` :
        `System timezone (${systemTimezone})`;
    const activeTimezone = timezoneOverride ?? systemTimezone;
    const mobileTimezoneLabel = activeTimezone.replace(/_/g, ' ');
    const isAvailabilityEnabled = Boolean(flags.team_availability?.enabled) || FORCE_TEAM_AVAILABILITY;
    const { mutate: setPresence } = useSetPresence();
    const isAway = isUserAway(loggedInUser);

    const presenceActionLabel = isAway ? 'Set to Online' : 'Set yourself away';

    const handlePresenceClick = () => {
        setPresence({ presence: isAway ? EPresence.ONLINE : EPresence.AWAY });
        closeNavigationDrawer();
    };

    const handleClickReportABug = async () => {
        const feedback = Sentry.getFeedback();
        const form = await feedback?.createForm();
        form?.appendToDom();
        form?.open();
    };

    const handleGoToSettings = () => {
        navigate('/landlord/inner/settings');
        closeNavigationDrawer();
    };

    return (
        <>
            <Drawer
                open
                variant="temporary"
                onClose={closeNavigationDrawer}
                sx={{ '& .MuiDrawer-paper': { width: '75%', maxWidth: 300 }, }}
            >
                <Box height="100vh">
                    <FlexColumn height="100%" justifyContent="space-between" alignItems="stretch">
                        <FlexColumn alignItems="stretch">
                            <Box justifyContent="center" alignItems="center">
                                <Link href={LANDLORD_HOME_PATH} onClick={closeNavigationDrawer}>
                                    <Flex height={60} p={1} pl={2} alignItems="center">
                                        <img
                                            src={reffieLogosBrainTransparent256}
                                            alt="reffie-logo"
                                            height="40px"
                                        />
                                        <Typography color="primary">
                                        Reffie
                                        </Typography>
                                    </Flex>
                                </Link>
                            </Box>

                            <List>
                                <DesktopNavigationInboxesLink onNavigationChange={closeNavigationDrawer} isSidebarCollapsed={false} />
                                {links.map((link) => {
                                    if (link.text === 'Properties') {
                                        return (
                                            <DesktopNavigationPropertiesLink
                                                key="properties"
                                                onNavigationChange={closeNavigationDrawer}
                                            />
                                        );
                                    }
                                    if (link.text === 'Team' && isAvailabilityEnabled) {
                                        return (
                                            <DesktopNavigationTeamLink
                                                key="team"
                                                isSidebarCollapsed={false}
                                                onNavigationChange={closeNavigationDrawer}
                                            />
                                        );
                                    }
                                    return (
                                        <NavigationLink
                                            key={link.text}
                                            text={link.text}
                                            icon={link.icon}
                                            link={link.link}
                                            onClick={closeNavigationDrawer}
                                        />
                                    );
                                })}
                            </List>
                        </FlexColumn>

                        <List>
                            {loggedInUser && (
                                <div className={navbarStyles.userSectionWrap}>
                                    <button
                                        className={navbarStyles.userSection}
                                        onClick={handleGoToSettings}
                                        data-testid="mobile-user-account-button"
                                    >
                                        <span className={navbarStyles.avatarWrapper}>
                                            <ProfileImageRounded url={loggedInUser.profile_photo_url} userName={loggedInUser.name} width={36} height={36} />
                                            {isAvailabilityEnabled && (
                                                <span className={clsx(navbarStyles.avatarRing, isAway ? navbarStyles.avatarRingAway : navbarStyles.avatarRingOnline)} />
                                            )}
                                        </span>
                                        <div className={navbarStyles.userInfo}>
                                            <Typography variant="body2" className={clsx(navbarStyles.userName, 'ellipsisText')}>
                                                {loggedInUser.name}
                                            </Typography>
                                            {loggedInUser?.team?.name && (
                                                <Tooltip title={loggedInUser.team.name}>
                                                    <Typography variant="caption" className={clsx(navbarStyles.userTeam, 'ellipsisText')}>
                                                        {loggedInUser.team.name}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                            <Tooltip title={timezoneLabel}>
                                                <div className={navbarStyles.timezonePill}>
                                                    <Globe size={12} className={navbarStyles.timezoneGlobeIcon} />
                                                    <Typography variant="caption" className={navbarStyles.userTimezone}>
                                                        {mobileTimezoneLabel}
                                                    </Typography>
                                                </div>
                                            </Tooltip>
                                        </div>
                                    </button>
                                </div>
                            )}
                            {isAvailabilityEnabled && (
                                <NavigationLink
                                    key="presence-toggle"
                                    text={<span className={navbarStyles.presenceToggleText}>{presenceActionLabel}</span>}
                                    icon={<span className={navbarStyles.presenceToggleIcon}><PresenceDot presence={isAway ? EPresence.AWAY : EPresence.ONLINE} size={10} /></span>}
                                    onClick={handlePresenceClick}
                                />
                            )}
                            <NavigationLink
                                key="report-a-bug-desktop"
                                text={'Report a Bug'}
                                icon={<Bug size={22} />}
                                link=""
                                onClick={handleClickReportABug}
                            />
                            <NavigationLink
                                text="Logout"
                                icon={<LogOutIcon />}
                                onClick={handleLogout}
                            />
                        </List>
                    </FlexColumn>
                </Box>
            </Drawer>
        </>
    );
}
